// Nails by Asha — the accountant pack.
//
// buildBooksWorkbook(...) → an XLSX workbook object (pure, testable in Node)
// exportBooks(...)        → the same workbook, downloaded
// workbookBase64(...)     → the same bytes, for emailing through nails-sb
//
// One workbook, four sheets, written for someone who has never seen this app:
//   Summary   — the P&L for the period, and the statement that no GST is involved
//   Income    — every job, in date order
//   Expenses  — every cost, in date order, saying whether a receipt is held
//   Pre-start — costs incurred before trading began on 1 Jul 2026, on their own
//
// The pre-start sheet exists because those costs belong to an earlier financial year
// and their treatment is a judgement call. Handing them over separately, at their true
// dates, lets the accountant make that call. Folding them into the trading year would
// be the app quietly making it instead.
import * as XLSX from 'xlsx';
import { summarise, preStartExpenses, money, fyLabel, PAYMENT_METHODS, TRADING_START } from './nailsBooks';

const BUSINESS = 'Nails by Asha';
const TRADER = 'Asha Bouveng';
const ABN = '60 119 559 804';

const methodLabel = (v) => (PAYMENT_METHODS.find((m) => m.value === v) || {}).label || v || '';
const dateAu = (s) => {
  const t = (s || '').toString().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return '';
  return `${t.slice(8, 10)}/${t.slice(5, 7)}/${t.slice(0, 4)}`;
};

const sheet = (aoa, widths) => {
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  if (widths) ws['!cols'] = widths.map((w) => ({ wch: w }));
  return ws;
};

export function buildBooksWorkbook({ income = [], expenses = [], period = {}, periodLabel = '' } = {}) {
  const s = summarise(income, expenses, period);
  const wb = XLSX.utils.book_new();

  // ── Summary ────────────────────────────────────────────────────────────────
  const sum = [
    [BUSINESS],
    [`${TRADER} — ABN ${ABN} — sole trader`],
    ['Not registered for GST. No GST is charged on income; expenses are recorded GST-inclusive and no input tax credits are claimed.'],
    [],
    ['Period', periodLabel || `${dateAu(period.from)} – ${dateAu(period.to)}`],
    // Local date: toISOString() would stamp the pack a day early all morning in Sydney.
    ['Prepared', dateAu(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`)],
    [],
    ['Profit & loss'],
    ['Income received', s.income.total],
    ['Expenses paid', s.expenses.total],
    ['Net', s.net],
    [],
    ['Jobs in period', s.income.count],
    ['Average per job', s.averageJob],
    ['Expenses with no receipt held', s.expenses.missingReceipts.length],
    [],
    ['Income by payment method'],
    ...s.income.byMethod.map((g) => [methodLabel(g.key), g.total]),
    [],
    ['Expenses by category'],
    ...s.expenses.byCategory.map((g) => [g.key, g.total]),
    [],
    ['Month by month'],
    ['Month', 'Income', 'Expenses', 'Net'],
    ...s.byMonth.map((m) => [m.month, m.income, m.expenses, m.net]),
  ];
  XLSX.utils.book_append_sheet(wb, sheet(sum, [34, 14, 14, 14]), 'Summary');

  // ── Income ─────────────────────────────────────────────────────────────────
  const incRows = s.income.rows.slice().sort((a, b) => (a.jobDate || '').localeCompare(b.jobDate || ''));
  const inc = [
    ['Date', 'Client', 'Description', 'Paid by', 'Amount', 'Note'],
    ...incRows.map((r) => [dateAu(r.jobDate), r.client || '', r.description || '', methodLabel(r.paymentMethod), Number(r.amount) || 0, r.note || '']),
    [],
    ['', '', '', 'Total', s.income.total, ''],
  ];
  XLSX.utils.book_append_sheet(wb, sheet(inc, [12, 22, 34, 14, 12, 30]), 'Income');

  // ── Expenses ───────────────────────────────────────────────────────────────
  const expRows = s.expenses.rows.slice().sort((a, b) => (a.spendDate || '').localeCompare(b.spendDate || ''));
  const exp = [
    ['Date', 'Supplier', 'Category', 'Amount', 'Receipt held', 'Note'],
    ...expRows.map((r) => [dateAu(r.spendDate), r.supplier || '', r.category || '', Number(r.amount) || 0, r.hasReceipt ? 'Yes' : 'NO', r.note || '']),
    [],
    ['', '', 'Total', s.expenses.total, '', ''],
  ];
  XLSX.utils.book_append_sheet(wb, sheet(exp, [12, 24, 20, 12, 13, 30]), 'Expenses');

  // ── Pre-start ──────────────────────────────────────────────────────────────
  // Always written, even when empty, so its absence is never read as "there were none".
  const pre = preStartExpenses(expenses);
  const preTotal = pre.reduce((t, r) => t + Math.round((Number(r.amount) || 0) * 100), 0) / 100;
  const preSheet = [
    ['Costs incurred before trading began'],
    [`Trading started ${dateAu(TRADING_START)}. The costs below were incurred before that date and are listed separately for treatment.`],
    [],
    ['Date', 'Supplier', 'Category', 'Amount', 'Receipt held', 'Note'],
    ...pre.map((r) => [dateAu(r.spendDate), r.supplier || '', r.category || '', Number(r.amount) || 0, r.hasReceipt ? 'Yes' : 'NO', r.note || '']),
    [],
    ['', '', 'Total', Math.round(preTotal * 100) / 100, '', ''],
  ];
  XLSX.utils.book_append_sheet(wb, sheet(preSheet, [12, 24, 20, 12, 13, 30]), 'Pre-start');

  return wb;
}

export const booksFilename = (periodLabel) =>
  `Nails by Asha - income and expenses${periodLabel ? ' - ' + periodLabel.replace(/[\\/:*?"<>|]/g, ' ') : ''}.xlsx`;

export function exportBooks(args) {
  const wb = buildBooksWorkbook(args);
  const name = booksFilename(args.periodLabel);
  XLSX.writeFile(wb, name);
  return name;
}

// base64 of the very same workbook, so what the accountant is emailed is byte-identical
// to what the Download button produces.
export function workbookBase64(args) {
  return XLSX.write(buildBooksWorkbook(args), { bookType: 'xlsx', type: 'base64' });
}

export { fyLabel, money };
